import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ForumAPI, getApiBaseUrl } from '../utils/api';
import { getToken } from '../utils/auth';
import ForumPostCard from '../components/ForumPostCard';
import ForumCategoryRow from '../components/ForumCategoryRow';
import { FaPlus, FaBullhorn, FaBookOpen, FaComments, FaArrowLeft, FaShieldAlt, FaUserGraduate, FaUsers, FaChevronDown, FaChevronUp, FaClipboardCheck, FaFolderPlus } from 'react-icons/fa';
import { toast } from 'react-hot-toast';
import CreateCategoryModal from '../components/Admin/CreateCategoryModal';
import ConfirmModal from '../components/ConfirmModal';

const ForumPage: React.FC = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [news, setNews] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [canCreate, setCanCreate] = useState(false);
    // canApprove now is also used to check if user is admin/mod/bqt
    const [canApprove, setCanApprove] = useState(false);
    const [isAdmin, setIsAdmin] = useState(false);
    const [isBCS, setIsBCS] = useState(false);
    const [categories, setCategories] = useState<any[]>([]);
    const [showCreateCategoryModal, setShowCreateCategoryModal] = useState(false);
    const [editingCategory, setEditingCategory] = useState<any>(null);
    const [confirmModal, setConfirmModal] = useState<{
        isOpen: boolean;
        title: string;
        message: string;
        onConfirm: () => void;
    }>({ isOpen: false, title: '', message: '', onConfirm: () => { } });

    const [optimizing, setOptimizing] = useState(false);
    // View Mode: 'index' (Forum List) or 'category' (Topic List)
    const [viewMode, setViewMode] = useState<'index' | 'category' | 'search'>('index');
    const [searchQuery, setSearchQuery] = useState('');
    const [userRole, setUserRole] = useState<string>('');

    useEffect(() => {
        const token = getToken();
        if (token) {
            import('../utils/api').then(({ AuthAPI }) => {
                AuthAPI.me(token).then(res => {
                    const role = res.user.role || '';
                    setUserRole(role);
                }).catch(() => { });
            });
        }
    }, []);
    const [activeTab, setActiveTab] = useState('all');

    // Section Toggle State
    const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
        admin: true,
        study: true,
        general: true
    });

    const toggleSection = (section: string) => {
        setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
    };

    // Categories are now fetched from API
    const loadCategories = async () => {
        try {
            const { ForumCategoriesAPI, getApiBaseUrl } = await import('../utils/api');
            const data = await ForumCategoriesAPI.list();

            // Helper for URL processing (Avatar)
            const API_URL = getApiBaseUrl();
            const baseUrl = API_URL.split('/api')[0];
            const processUrl = (url: string) =>
                url && url.startsWith('http') ? url : url ? `${baseUrl}${url}` : '';

            setCategories(data.categories.map((c: any) => ({
                ...c,
                // Map icon string to React element
                icon: getIconComponent(c.icon),
                // Map color string to Tailwind classes
                bg: `bg-${c.color}-50 dark:bg-${c.color}-900/10`,
                color: `text-${c.color}-600 bg-${c.color}-100 dark:text-${c.color}-400 dark:bg-${c.color}-900`,
                // Stats from Backend
                stats: { topics: c.topic_count || 0 },
                latestPost: c.latest_post ? {
                    ...c.latest_post,
                    time: c.latest_post.time, // Ensure naming matches
                    author: {
                        ...c.latest_post.author,
                        avatar: c.latest_post.author.avatar ? processUrl(c.latest_post.author.avatar) : null
                    }
                } : null
            })));

            // If we are in index mode, we can stop loading now
            if (!searchParams.get('category')) {
                setLoading(false);
            }

        } catch (error) {
            console.error('Failed to load categories', error);
            setLoading(false);
        }
    };

    const getIconComponent = (iconName: string) => {
        switch (iconName) {
            case 'FaBullhorn': return <FaBullhorn />;
            case 'FaBookOpen': return <FaBookOpen />;
            case 'FaComments': return <FaComments />;
            case 'FaShieldAlt': return <FaShieldAlt />;
            case 'FaUserGraduate': return <FaUserGraduate />;
            case 'FaUsers': return <FaUsers />;
            default: return <FaFolderPlus />;
        }
    };

    useEffect(() => {
        loadCategories();
        checkPermission();

        // Check URL parameters for category
        const categoryParam = searchParams.get('category');
        if (categoryParam) {
            setActiveTab(categoryParam);
            setViewMode('category');
            loadNews(categoryParam);
        } else {
            // Index mode: Categories loaded above handle the UI.
            // No need to load news.
            setViewMode('index');
            // Check Permissions doesn't trigger loading, so relying on loadCategories to finish loading
        }
    }, [searchParams]); // Added searchParams dependency

    const loadNews = async (categoryFilter?: string) => {
        try {
            setLoading(true);
            const token = getToken();
            if (!token) {
                navigate('/login');
                return;
            }

            const targetCategory = categoryFilter || (viewMode === 'category' ? activeTab : undefined);
            if (!targetCategory && viewMode === 'index') return; // Should not happen

            const response = await ForumAPI.list({
                limit: 20, // Optimized limit
                offset: 0,
                category: targetCategory // Only fetch for this category
            }, token);

            // Process image URLs
            const { getApiBaseUrl } = await import('../utils/api');
            const API_URL = getApiBaseUrl();
            const baseUrl = API_URL.split('/api')[0];
            const processUrl = (url: string) =>
                url && url.startsWith('http') ? url : url ? `${baseUrl}${url}` : '';

            const processedNews = response.news.map((newsItem: any) => ({
                ...newsItem,
                image: newsItem.image ? processUrl(newsItem.image) : null,
                author: {
                    ...newsItem.author,
                    avatar: newsItem.author?.avatar ? processUrl(newsItem.author.avatar) : null
                }
            }));

            setNews(processedNews);
        } catch (error: any) {
            console.error('Load news error:', error);
            toast.error('Không thể tải tin tức');
        } finally {
            setLoading(false);
        }
    };

    const checkPermission = async () => {
        try {
            const token = getToken();
            setCanCreate(!!token);

            if (token) {
                const payload = JSON.parse(atob(token.split('.')[1]));
                const userRole = payload.role;
                setCanApprove(userRole === 'admin' || userRole === 'moderator' || userRole === 'bqt');
                setIsAdmin(userRole === 'admin');

                // BCS Roles
                const bcsRoles = ['lop_truong', 'bi_thu', 'pho_bi_thu', 'lop_pho_hoc_tap', 'lop_pho_doi_song', 'thu_quy'];
                // Since token usually has one role, check if it's in the list
                // For users with multiple roles, payload might be different but let's assume standard structure or primary role for now.
                // If the user has a secondary role table, the `auth/me` endpoint should be used ideally, but for now we trust the token's role claim.
                setIsBCS(bcsRoles.includes(userRole));
            }
        } catch (error) {
            console.error('Check permission error:', error);
        }
    };

    // getStats removed - stats are now provided by categories API

    const handleCategoryClick = (categoryName: string) => {
        setActiveTab(categoryName);
        setViewMode('category');
        navigate(`/forum?category=${categoryName}`);
    };

    const handleBackToIndex = () => {
        setViewMode('index');
        navigate('/forum');
    };

    const handleOptimize = async () => {
        if (!window.confirm('Hành động này sẽ tối ưu hóa cơ sở dữ liệu để tăng tốc độ. Bạn có chắc chắn muốn tiếp tục?')) return;

        try {
            setOptimizing(true);
            const token = getToken();
            if (!token) return;

            // Call the admin optimization endpoint directly
            const response = await fetch(`${getApiBaseUrl()}/admin/optimize-db`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            const data = await response.json();

            if (response.ok && data.success) {
                toast.success('Đã tối ưu hóa hệ thống thành công! 🚀');
                // Reload data to reflect performance
                loadCategories();
                if (viewMode === 'category') loadNews(activeTab);
            } else {
                toast.error('Có lỗi xảy ra: ' + (data.message || 'Unknown error'));
            }
        } catch (error) {
            console.error('Optimization error:', error);
            toast.error('Không thể tối ưu hóa hệ thống');
        } finally {
            setOptimizing(false);
        }
    };

    const handleEditCategory = (category: any) => {
        setEditingCategory(category);
        setShowCreateCategoryModal(true);
    };

    const handleDeleteCategory = (category: any) => {
        setConfirmModal({
            isOpen: true,
            title: 'Xóa chuyên mục?',
            message: `Bạn có chắc muốn xóa chuyên mục "${category.label}"? Các bài viết trong mục này sẽ được chuyển sang "Thảo Luận Chung". Hành động này không thể hoàn tác.`,
            onConfirm: () => executeDeleteCategory(category.id)
        });
    };

    const executeDeleteCategory = async (categoryId: string) => {
        try {
            const token = getToken();
            if (!token) return;

            const { ForumCategoriesAPI } = await import('../utils/api');
            await ForumCategoriesAPI.delete(categoryId, token);
            toast.success('Đã xóa chuyên mục');
            loadCategories();
            setConfirmModal(prev => ({ ...prev, isOpen: false }));
        } catch (error: any) {
            console.error('Delete category error:', error);
            toast.error(error.message || 'Không thể xóa chuyên mục');
        }
    };

    return (
        <div className="min-h-screen bg-transparent">
            {/* Hero Header */}
            <div className="relative overflow-hidden group bg-gradient-to-br from-blue-600 via-blue-700 to-blue-900 dark:from-blue-900 dark:via-slate-900 dark:to-slate-950 shadow-2xl animate-slideDownIn">
                <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full blur-3xl mix-blend-overlay"></div>
                <div className="absolute bottom-0 left-0 w-96 h-96 bg-blue-400/10 rounded-full blur-3xl mix-blend-overlay"></div>
                <div className="absolute inset-0 opacity-[0.08] bg-[radial-gradient(circle_at_1px_1px,_#fff_1px,_transparent_0)] bg-[size:24px_24px] pointer-events-none"></div>

                <div className="w-full mx-auto p-8 sm:p-12">
                    <div className="relative z-10">
                        <h1 className="text-2xl sm:text-3xl lg:text-4xl font-mono font-bold text-white mb-4 tracking-tight">
                            Diễn đàn
                        </h1>
                        <p className="text-base font-mono sm:text-lg text-blue-100 dark:text-blue-200 max-w-2xl leading-relaxed">
                            {viewMode === 'index'
                                ? 'Khu vực trao đổi, thảo luận và cập nhật thông báo của Lớp.'
                                : categories.find(c => c.name === activeTab)?.label || 'Danh sách bài viết'}
                        </p>
                    </div>
                </div>
            </div>

            <div className="w-full mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8 animate-fadeIn">

                {/* Actions Bar */}
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
                    {viewMode === 'category' ? (
                        <button
                            onClick={handleBackToIndex}
                            className="inline-flex items-center gap-2 text-gray-500 hover:text-blue-600 dark:text-gray-400 dark:hover:text-blue-400 transition-colors font-medium group"
                        >
                            <div className="p-2 bg-white dark:bg-gray-800 rounded-full shadow-sm group-hover:shadow-md transition-all border border-gray-200 dark:border-gray-700">
                                <FaArrowLeft className="group-hover:-translate-x-0.5 transition-transform text-sm" />
                            </div>
                            <span>Quay lại</span>
                        </button>
                    ) : (
                        <div className="text-sm text-gray-500 dark:text-gray-400 font-medium">
                            Tổng số: <span className="text-blue-600 dark:text-blue-400">{news.length}</span> bài viết
                        </div>
                    )}

                    <div className="flex flex-wrap gap-2 w-full sm:w-auto">
                        {canApprove && (
                            <button
                                onClick={() => navigate('/forum/approval')}
                                className="
                            flex flex-1 sm:flex-none items-center justify-center gap-2 px-4 sm:px-5 py-2.5 
                            bg-orange-600 hover:bg-orange-700 text-white rounded-lg font-bold 
                            transition-all shadow-sm hover:shadow-md active:scale-95 text-sm whitespace-nowrap
                        "
                            >
                                <FaClipboardCheck /> Duyệt bài
                            </button>
                        )}

                        {/* Admin: Create Category on Index */}
                        {isAdmin && viewMode === 'index' && (
                            <button
                                onClick={() => setShowCreateCategoryModal(true)}
                                className="
                                flex flex-1 sm:flex-none items-center justify-center gap-2 px-4 sm:px-5 py-2.5 
                                bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg font-bold 
                                transition-all shadow-sm hover:shadow-md active:scale-95 text-sm whitespace-nowrap
                            "
                            >
                                <FaPlus /> Tạo chuyên mục
                            </button>
                        )}

                        {/* Regular User OR Admin inside Category: Create News */}
                        {/* Regular User OR Admin inside Category: Create News */}
                        {canCreate && viewMode === 'category' && (() => {
                            const currentCat = categories.find(c => c.name === activeTab);
                            if (!currentCat) return null;

                            // Permission Logic
                            if (currentCat.section === 'admin' && !isAdmin && !isBCS) return null;
                            if (currentCat.section === 'study' && !isAdmin && !isBCS) return null;
                            // General: Everyone can see button (but approval logic applies on backend)

                            return (
                                <div className="flex gap-3">
                                    <button
                                        onClick={() => navigate(`/forum/create?category=${activeTab}`)}
                                        className="flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-bold shadow-lg shadow-blue-500/30 transition-all hover:scale-105 active:scale-95"
                                    >
                                        <FaPlus />
                                        <span className="hidden sm:inline">Tạo bài viết</span>
                                    </button>
                                </div>
                            );
                        })()}
                    </div>
                </div>

                {loading ? (
                    <div className="py-20 flex items-center justify-center">
                        {(() => {
                            const SpinnerLoading = require('../components/SpinnerLoading').default;
                            return <div style={{ transform: 'scale(0.435)' }}><SpinnerLoading /></div>;
                        })()}
                    </div>
                ) : (
                    <>
                        {/* VIEW MODE: INDEX */}
                        {viewMode === 'index' && (
                            <div className="space-y-8 animate-fadeIn">
                                {/* KHU VỰC ĐIỀU HÀNH */}
                                <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700">
                                    <div
                                        className="bg-gray-100 dark:bg-gray-700 px-5 py-3 border-b border-gray-200 dark:border-gray-600 flex justify-between items-center cursor-pointer select-none ring-1 ring-black/5 dark:ring-white/5"
                                        onClick={() => toggleSection('admin')}
                                    >
                                        <div className="flex items-center gap-2 font-bold text-gray-900 dark:text-gray-100 uppercase text-xs tracking-wider">
                                            <FaShieldAlt className="text-red-600" />
                                            Khu Vực Điều Hành
                                        </div>
                                        {expandedSections.admin ? <FaChevronDown className="text-gray-400 text-xs" /> : <FaChevronUp className="text-gray-400 text-xs" />}
                                    </div>

                                    {expandedSections.admin && (
                                        <div>
                                            {categories.filter(c => c.section === 'admin').map(cat => (
                                                <ForumCategoryRow
                                                    key={cat.id}
                                                    {...cat}
                                                    title={cat.label}
                                                    description={cat.description}
                                                    stats={cat.stats}
                                                    latestPost={cat.latestPost}
                                                    onClick={() => handleCategoryClick(cat.name)}
                                                    onLatestPostClick={(id) => navigate(`/forum/${id}`)}
                                                    onEdit={isAdmin ? () => handleEditCategory(cat) : undefined}
                                                    onDelete={isAdmin ? () => handleDeleteCategory(cat) : undefined}
                                                />
                                            ))}
                                        </div>
                                    )}
                                </div>

                                {/* GÓC HỌC TẬP */}
                                <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700">
                                    <div
                                        className="bg-gray-100 dark:bg-gray-700 px-5 py-3 border-b border-gray-200 dark:border-gray-600 flex justify-between items-center cursor-pointer select-none ring-1 ring-black/5 dark:ring-white/5"
                                        onClick={() => toggleSection('study')}
                                    >
                                        <div className="flex items-center gap-2 font-bold text-gray-900 dark:text-gray-100 uppercase text-xs tracking-wider">
                                            <FaUserGraduate className="text-blue-600" />
                                            Góc Học Tập & Tài Liệu
                                        </div>
                                        {expandedSections.study ? <FaChevronDown className="text-gray-400 text-xs" /> : <FaChevronUp className="text-gray-400 text-xs" />}
                                    </div>

                                    {expandedSections.study && (
                                        <div>
                                            {categories.filter(c => c.section === 'study').map(cat => (
                                                <ForumCategoryRow
                                                    key={cat.id}
                                                    {...cat}
                                                    title={cat.label}
                                                    description={cat.description}
                                                    stats={cat.stats}
                                                    latestPost={cat.latestPost}
                                                    onClick={() => handleCategoryClick(cat.name)}
                                                    onLatestPostClick={(id) => navigate(`/forum/${id}`)}
                                                    onEdit={isAdmin ? () => handleEditCategory(cat) : undefined}
                                                    onDelete={isAdmin ? () => handleDeleteCategory(cat) : undefined}
                                                />
                                            ))}
                                        </div>
                                    )}
                                </div>

                                {/* THẢO LUẬN */}
                                <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700">
                                    <div
                                        className="bg-gray-100 dark:bg-gray-700 px-5 py-3 border-b border-gray-200 dark:border-gray-600 flex justify-between items-center cursor-pointer select-none ring-1 ring-black/5 dark:ring-white/5"
                                        onClick={() => toggleSection('general')}
                                    >
                                        <div className="flex items-center gap-2 font-bold text-gray-900 dark:text-gray-100 uppercase text-xs tracking-wider">
                                            <FaUsers className="text-green-600" />
                                            Cộng Đồng & Thảo Luận
                                        </div>
                                        {expandedSections.general ? <FaChevronDown className="text-gray-400 text-xs" /> : <FaChevronUp className="text-gray-400 text-xs" />}
                                    </div>

                                    {expandedSections.general && (
                                        <div>
                                            {categories.filter(c => c.section === 'general').map(cat => (
                                                <ForumCategoryRow
                                                    key={cat.id}
                                                    {...cat}
                                                    title={cat.label}
                                                    description={cat.description}
                                                    stats={cat.stats}
                                                    latestPost={cat.latestPost}
                                                    onClick={() => handleCategoryClick(cat.name)}
                                                    onLatestPostClick={(id) => navigate(`/forum/${id}`)}
                                                    onEdit={isAdmin ? () => handleEditCategory(cat) : undefined}
                                                    onDelete={isAdmin ? () => handleDeleteCategory(cat) : undefined}
                                                />
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* VIEW MODE: CATEGORY LIST */}
                        {viewMode === 'category' && (
                            <div className="animate-fadeIn">
                                {news.filter(item => item.category === activeTab).length === 0 ? (
                                    <div className="text-center py-20 bg-white dark:bg-gray-800 rounded-3xl border border-dashed border-gray-200 dark:border-gray-700">
                                        <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gray-50 dark:bg-gray-700/50 mb-4 animate-pulse">
                                            <FaBookOpen className="text-4xl text-gray-400" />
                                        </div>
                                        <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
                                            Chưa có bài viết nào
                                        </h3>
                                        <p className="text-gray-500 dark:text-gray-400 max-w-sm mx-auto">
                                            Chuyên mục này chưa có bài viết nào. Hãy là người đầu tiên đăng bài!
                                        </p>
                                    </div>
                                ) : (
                                    <div className="grid grid-cols-1 gap-3">
                                        {news.filter(item => item.category === activeTab).map((item) => (
                                            <ForumPostCard key={item.id} news={item} variant="list" currentCategory={activeTab} />
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}
                    </>
                )}
            </div>
            {/* Create Category Modal */}
            {showCreateCategoryModal && (
                <CreateCategoryModal
                    initialData={editingCategory}
                    onClose={() => {
                        setShowCreateCategoryModal(false);
                        setEditingCategory(null);
                    }}
                    onSuccess={() => {
                        setShowCreateCategoryModal(false);
                        setEditingCategory(null);
                        loadCategories();
                    }}
                />
            )}
            {/* Confirm Modal */}
            <ConfirmModal
                isOpen={confirmModal.isOpen}
                onClose={() => setConfirmModal(prev => ({ ...prev, isOpen: false }))}
                onConfirm={confirmModal.onConfirm}
                title={confirmModal.title}
                message={confirmModal.message}
                confirmText="Xóa chuyên mục"
                type="danger"
            />
        </div>
    );
};

export default ForumPage;
